import { Alert, Button, Checkbox, Col, Collapse, Divider, Form, InputNumber, Row, Select, Space, Statistic, Switch, Tag, Typography, message } from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import { useWisecureMutation } from '@wisecure/api-client/hooks';
import type {
  AdminSecurityEstimateResponse,
  SecurityConfigurationDto,
  SecurityFormula,
  SecurityLevels,
  SecurityPropertyType,
  SecuritySurfaceRange,
} from '@wisecure/api-client';
import { WisecureCard } from '@/components/base';
import { error } from '@/theme';
import { EstimateEquipment, FormulaPrices } from './EstimateEquipment';
import { InternalMarginBreakdown } from './InternalMarginBreakdown';
import {
  FORMULA_LABELS,
  LEVELS_LABELS,
  PROPERTY_TYPE_LABELS,
  STUDY_REASON_LABELS,
  SURFACE_LABELS,
  formatEuro,
  formatHalfDays,
  toOptions,
} from './labels';

const { Text } = Typography;

interface SimulatorValues {
  propertyType: SecurityPropertyType;
  surface: SecuritySurfaceRange;
  levels: SecurityLevels;
  alarm: boolean;
  alarmFormula: SecurityFormula;
  motionZones: number;
  openings: number;
  garage: boolean;
  exteriorDetection: boolean;
  video: boolean;
  videoFormula: SecurityFormula;
  entrance: number;
  exteriorCameras: number;
  interior: number;
  parking: number;
  cashDesk: number;
  smokeDetectors: number;
}

const INITIAL_VALUES: SimulatorValues = {
  propertyType: 'HOUSE',
  surface: 'R100_300',
  levels: 'MULTI',
  alarm: true,
  alarmFormula: 'CONFORT',
  motionZones: 3,
  openings: 7,
  garage: false,
  exteriorDetection: false,
  video: true,
  videoFormula: 'CONFORT',
  entrance: 1,
  exteriorCameras: 2,
  interior: 0,
  parking: 0,
  cashDesk: 0,
  smokeDetectors: 0,
};

function toConfiguration(v: SimulatorValues): SecurityConfigurationDto {
  const business = v.propertyType === 'BUSINESS';
  return {
    property: { type: v.propertyType, surface: v.surface, levels: v.levels },
    needs: { alarm: v.alarm, video: v.video, fire: false, access: false },
    alarm: v.alarm
      ? { motionZones: v.motionZones, openings: v.openings, garage: v.garage, exterior: v.exteriorDetection }
      : undefined,
    video: v.video
      ? {
          entrance: v.entrance,
          exterior: v.exteriorCameras,
          interior: v.interior,
          parking: business ? v.parking : 0,
          cashDesk: business ? v.cashDesk : 0,
        }
      : undefined,
    formulas: { alarm: v.alarmFormula, video: v.videoFormula },
    options: {
      smokeDetectors: v.smokeDetectors,
      floodDetectors: 0,
      remotes: 0,
      fogGenerators: 0,
      lightingModules: 0,
      gateModule: false,
      exteriorPhoto: true,
    },
  };
}

const formulaOptions = toOptions(FORMULA_LABELS);

function CountField({ name, label }: { name: keyof SimulatorValues; label: string }) {
  return (
    <Col xs={12} md={8}>
      <Form.Item name={name} label={label}>
        <InputNumber<number> min={0} max={99} precision={0} style={{ width: '100%' }} />
      </Form.Item>
    </Col>
  );
}

function SimulatorResult({ estimate }: { estimate: AdminSecurityEstimateResponse }) {
  return (
    <div>
      {estimate.studyRequired && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Le client serait orienté vers une étude sur site"
          description={estimate.studyReasons.map((r) => STUDY_REASON_LABELS[r] ?? r).join(', ')}
        />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12}>
          <Statistic title="Prix installé (client)" value={formatEuro(estimate.totalPrice)} valueStyle={{ fontWeight: 700 }} />
        </Col>
        <Col xs={12}>
          <Statistic title="Durée de pose" value={estimate.installHalfDays ? formatHalfDays(estimate.installHalfDays) : '—'} />
        </Col>
      </Row>
      <Space direction="vertical" size={8} style={{ marginBottom: 16 }}>
        {estimate.alarm && (
          <Space wrap>
            <Text strong>Alarme</Text>
            <FormulaPrices product={estimate.alarm} />
          </Space>
        )}
        {estimate.video && (
          <Space wrap>
            <Text strong>Vidéo</Text>
            <FormulaPrices product={estimate.video} />
          </Space>
        )}
        {estimate.options && (
          <Space wrap>
            <Text strong>Options</Text>
            <Tag>{formatEuro(estimate.options.price)}</Tag>
          </Space>
        )}
      </Space>
      <Divider orientation="left" orientationMargin={0}>
        <Text style={{ color: error[400] }}>Interne</Text>
      </Divider>
      <InternalMarginBreakdown internal={estimate.internal} compact />
      <Collapse
        style={{ marginTop: 16 }}
        size="small"
        items={[{ key: 'lines', label: 'Matériel détaillé', children: <EstimateEquipment estimate={estimate} /> }]}
      />
    </div>
  );
}

/** Computes an estimate with the current catalogue, to check prices after editing them. */
export function EstimateSimulatorCard() {
  const [form] = Form.useForm<SimulatorValues>();
  const alarm = Form.useWatch('alarm', form);
  const video = Form.useWatch('video', form);
  const propertyType = Form.useWatch('propertyType', form);

  const simulate = useWisecureMutation<AdminSecurityEstimateResponse, SecurityConfigurationDto>(
    (client, body) => client.dashboardSecurity.securityDashboardControllerEstimate({ requestBody: body }),
    { onError: () => message.error('Impossible de calculer cette estimation') },
  );

  // Hidden fields (alarm / video off, business only) are unmounted: read the whole store.
  const handleFinish = () =>
    simulate.mutate(toConfiguration({ ...INITIAL_VALUES, ...form.getFieldsValue(true) }));

  return (
    <WisecureCard
      title={
        <Space>
          <CalculatorOutlined />
          <span>Simulateur</span>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Form<SimulatorValues>
        form={form}
        layout="vertical"
        size="small"
        initialValues={INITIAL_VALUES}
        onFinish={handleFinish}
      >
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name="propertyType" label="Bien">
              <Select options={toOptions(PROPERTY_TYPE_LABELS)} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="surface" label="Surface">
              <Select options={toOptions(SURFACE_LABELS)} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="levels" label="Niveaux">
              <Select options={toOptions(LEVELS_LABELS)} />
            </Form.Item>
          </Col>
        </Row>

        <Space align="center" style={{ marginBottom: 8 }}>
          <Form.Item name="alarm" valuePropName="checked" noStyle>
            <Switch size="small" />
          </Form.Item>
          <Text strong>Alarme</Text>
          <Form.Item name="alarmFormula" noStyle>
            <Select options={formulaOptions} style={{ width: 120 }} disabled={!alarm} />
          </Form.Item>
        </Space>
        {alarm && (
          <Row gutter={12}>
            <CountField name="motionZones" label="Zones de mouvement" />
            <CountField name="openings" label="Ouvertures" />
            <Col xs={24} md={8}>
              <Form.Item name="garage" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>Garage / dépendance</Checkbox>
              </Form.Item>
              <Form.Item name="exteriorDetection" valuePropName="checked">
                <Checkbox>Détection extérieure</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        )}

        <Space align="center" style={{ marginBottom: 8 }}>
          <Form.Item name="video" valuePropName="checked" noStyle>
            <Switch size="small" />
          </Form.Item>
          <Text strong>Vidéo</Text>
          <Form.Item name="videoFormula" noStyle>
            <Select options={formulaOptions} style={{ width: 120 }} disabled={!video} />
          </Form.Item>
        </Space>
        {video && (
          <Row gutter={12}>
            <CountField name="entrance" label="Entrée" />
            <CountField name="exteriorCameras" label="Extérieur" />
            <CountField name="interior" label="Intérieur" />
            {propertyType === 'BUSINESS' && (
              <>
                <CountField name="parking" label="Parking" />
                <CountField name="cashDesk" label="Caisse / stock" />
              </>
            )}
          </Row>
        )}

        <Row gutter={12}>
          <CountField name="smokeDetectors" label="Détecteurs de fumée" />
        </Row>

        <Button type="primary" htmlType="submit" icon={<CalculatorOutlined />} loading={simulate.isPending} block>
          Calculer
        </Button>
      </Form>

      {simulate.data && (
        <>
          <Divider />
          <SimulatorResult estimate={simulate.data} />
        </>
      )}
    </WisecureCard>
  );
}
